import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { ONBOARDING_KINDS, ONBOARDING_STATUSES } from '../constants';
import type { OnboardingInput } from '../types';

const schema = z.object({
  employee: z.string().min(1, 'Employee is required'),
  kind: z.enum(['onboarding', 'offboarding']),
  status: z.enum(['todo', 'in_progress', 'completed']),
  started_on: z.string(),
  completed_on: z.string(),
  items: z.array(z.object({ title: z.string(), is_done: z.boolean() })),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['employee', 'kind', 'status', 'started_on', 'completed_on'] as const;

interface OnboardingFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  /** Show the seed-checklist editor (create only — items are edited on the detail page). */
  showChecklist?: boolean;
  onSubmit: (values: OnboardingInput) => Promise<unknown>;
  onCancel: () => void;
}

export function OnboardingForm({
  defaultValues,
  submitLabel,
  showChecklist = false,
  onSubmit,
  onCancel,
}: OnboardingFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      kind: 'onboarding',
      status: 'todo',
      started_on: new Date().toISOString().slice(0, 10),
      completed_on: '',
      items: [],
      ...defaultValues,
    },
  });

  const items = useFieldArray({ control: form.control, name: 'items' });

  async function handleSubmit(values: FormValues) {
    const input: OnboardingInput = {
      employee: Number(values.employee),
      kind: values.kind,
      status: values.status,
      started_on: values.started_on || null,
      completed_on: values.completed_on || null,
      // Drop blank rows; only seed items when the editor is shown (create).
      items: showChecklist
        ? values.items
            .map((it) => ({ title: it.title.trim(), is_done: it.is_done }))
            .filter((it) => it.title)
        : undefined,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        <FormSection variant="card" title="Employee" description="Who is this checklist for?">
          <FormField
            control={form.control}
            name="employee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="onboarding-employee-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchEmployees}
                    resolveLabel={resolveEmployeeLabel}
                    placeholder="Select an employee"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Plan" description="Kind, status, and dates.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kind</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ONBOARDING_KINDS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ONBOARDING_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid items-start gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="started_on"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Started</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="completed_on"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Completed
                    <InfoHint text="Leave blank if still in progress." />
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        {showChecklist ? (
          <FormSection
            variant="card"
            title="Checklist"
            description="Items to complete — tick any that are already done."
          >
            <div className="space-y-2">
              {items.fields.map((row, index) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Controller
                    control={form.control}
                    name={`items.${index}.is_done`}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="accent-primary size-4 shrink-0"
                        aria-label="Done"
                      />
                    )}
                  />
                  <Input
                    {...form.register(`items.${index}.title`)}
                    placeholder="e.g. Set up laptop & accounts"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => items.remove(index)}
                    aria-label="Remove item"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => items.append({ title: '', is_done: false })}
              >
                <Plus className="size-4" /> Add item
              </Button>
            </div>
          </FormSection>
        ) : null}

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
